
import { useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';
import AuthModal from '../../../components/auth/AuthModal';

export default function Reservation() {
  const { user } = useAuth();
  const [selectedPartner, setSelectedPartner] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [participants, setParticipants] = useState('1');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const partners = [
    { value: 'park-seongsuk', label: '박성숙 - 남도음식전문가' },
    { value: 'bok-yeongmun', label: '복영문 - 제이앤비농장 (유자농장)' },
    { value: 'jeong-jongkwon', label: '정종권 - 봄햇살케어팜 (숲체험)' },
    { value: 'choi-hyeongui', label: '최현귀 - 두울리 (라벤더농장)' },
    { value: 'park-jiyeong', label: '박지영 - 세모실커피농장' },
    { value: 'kim-eunjeong', label: '김은정 - 한옥펜션' },
    { value: 'son-hyeonsuk', label: '손현숙 - 하루애꽃 (치유체험농장)' }
  ];

  const timeSlots = [
    '09:00', '10:00', '11:00', '14:00', '15:00', '16:00'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 로그인 확인
    if (!user) {
      setIsAuthModalOpen(true);
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData(e.target as HTMLFormElement);
    const name = formData.get('name') as string;
    const phone = formData.get('phone') as string;
    const message = formData.get('message') as string;
    
    try {
      // Supabase에 예약 데이터 저장
      const { error } = await supabase
        .from('reservations')
        .insert([
          {
            user_id: user.id,
            partner_id: selectedPartner,
            partner_name: partners.find(p => p.value === selectedPartner)?.label || '',
            date: selectedDate,
            time: selectedTime,
            participants: parseInt(participants),
            name,
            phone,
            message: message || null,
            status: 'pending'
          }
        ]);

      if (error) throw error;

      setSubmitMessage('예약 신청이 성공적으로 접수되었습니다. 곧 연락드리겠습니다.');
      (e.target as HTMLFormElement).reset();
      setSelectedPartner('');
      setSelectedDate('');
      setSelectedTime('');
      setParticipants('1');
    } catch (error) {
      console.error('예약 저장 오류:', error);
      setSubmitMessage('예약 신청 중 오류가 발생했습니다. 다시 시도해주세요.');
    }

    setIsSubmitting(false);
    setTimeout(() => setSubmitMessage(''), 5000);
  };

  return (
    <>
      <section id="reservation" className="py-24 bg-gradient-to-b from-green-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-800 mb-6">예약하기</h3>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              원하시는 참여업체와 날짜를 선택하여 치유 프로그램을 예약해보세요
            </p>
            {!user && (
              <div className="mt-4 p-4 bg-yellow-100 text-yellow-700 rounded-xl max-w-md mx-auto">
                <i className="ri-information-line mr-2"></i>
                예약을 위해서는 로그인이 필요합니다.
              </div>
            )}
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              <div className="grid lg:grid-cols-2 gap-0">
                {/* Form Section */}
                <div className="p-8 lg:p-12">
                  <h4 className="text-2xl font-bold text-gray-800 mb-8">예약 정보 입력</h4>
                  
                  <form id="reservation-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                    {/* 참여업체 선택 */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        참여업체 선택 *
                      </label>
                      <div className="relative">
                        <select
                          name="partner"
                          value={selectedPartner}
                          onChange={(e) => setSelectedPartner(e.target.value)}
                          required
                          className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm appearance-none bg-white"
                        >
                          <option value="">참여업체를 선택해주세요</option>
                          {partners.map((partner) => (
                            <option key={partner.value} value={partner.value}>
                              {partner.label}
                            </option>
                          ))}
                        </select>
                        <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                      </div>
                    </div>

                    {/* 날짜 선택 */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        희망 날짜 *
                      </label>
                      <input
                        type="date"
                        name="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      />
                    </div>

                    {/* 시간 선택 */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        희망 시간 *
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {timeSlots.map((time) => (
                          <button
                            key={time}
                            type="button"
                            onClick={() => setSelectedTime(time)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                              selectedTime === time
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-green-100'
                            }`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                      <input type="hidden" name="time" value={selectedTime} />
                    </div>

                    {/* 참가 인원 */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        참가 인원 *
                      </label>
                      <select
                        name="participants"
                        value={participants}
                        onChange={(e) => setParticipants(e.target.value)}
                        required
                        className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm appearance-none bg-white"
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                          <option key={num} value={num}>
                            {num}명
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* 연락처 정보 */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          이름 *
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          defaultValue={user?.user_metadata?.name || ''}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                          placeholder="이름을 입력해주세요"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          연락처 *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          defaultValue={user?.user_metadata?.phone || ''}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                          placeholder="010-0000-0000"
                        />
                      </div>
                    </div>

                    {/* 특별 요청사항 */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        특별 요청사항
                      </label>
                      <textarea
                        name="message"
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm resize-none"
                        placeholder="특별한 요청사항이나 문의사항을 입력해주세요 (최대 500자)"
                      ></textarea>
                    </div>

                    {/* 제출 버튼 */}
                    <button
                      type="submit"
                      disabled={isSubmitting || !selectedPartner || !selectedDate || !selectedTime}
                      className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white py-4 rounded-xl text-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      {isSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          예약 신청 중...
                        </>
                      ) : !user ? (
                        <>
                          <i className="ri-login-line mr-2"></i>
                          로그인 후 예약하기
                        </>
                      ) : (
                        <>
                          <i className="ri-calendar-check-line mr-2"></i>
                          예약 신청하기
                        </>
                      )}
                    </button>

                    {/* 제출 메시지 */}
                    {submitMessage && (
                      <div className={`p-4 rounded-xl text-center ${
                        submitMessage.includes('성공') 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {submitMessage}
                      </div>
                    )}
                  </form>
                </div>

                {/* Info Section */}
                <div className="bg-green-50 p-8 lg:p-12 flex flex-col justify-center">
                  <h4 className="text-2xl font-bold text-gray-800 mb-6">예약 안내</h4>
                  
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-800 mb-1">운영 시간</h5>
                        <p className="text-gray-600 text-sm">오전 9시 ~ 오후 6시 (점심시간 12-13시 제외)</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-calendar-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-800 mb-1">예약 확정</h5>
                        <p className="text-gray-600 text-sm">예약 신청 후 24시간 내 연락드립니다</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-group-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-800 mb-1">최소 인원</h5>
                        <p className="text-gray-600 text-sm">프로그램별 최소 2명 이상 권장</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-white text-sm"></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-800 mb-1">문의 전화</h5>
                        <p className="text-gray-600 text-sm">061-830-5000</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 p-4 bg-white rounded-xl">
                    <p className="text-sm text-gray-600 leading-relaxed">
                      <i className="ri-information-line text-green-500 mr-2"></i>
                      날씨나 농장 상황에 따라 프로그램이 변경될 수 있습니다. 
                      예약 확정 시 자세한 안내를 드리겠습니다.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 인증 모달 */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        defaultMode="login"
      />
    </>
  );
}
